/**
 * BaaS fiat transactions via MobileService `/v1/ib/console/*` → account statements (+ optional Pix).
 * Simulate/release/refund helpers remain local (HML demo) — no BaaS admin action API yet.
 */

import {
  BAAS_STATEMENT_PAGE_SIZE,
  MAX_BAAS_STATEMENT_ITEMS,
  fetchAccountStatements,
  fetchBaasAccounts,
  isValutSegregationWalletInstance,
  normalizeBaasStatementsQuery,
} from "./baasAccountsListApi";
import { fetchInternalTransferOptions } from "./baasInternalTransfersApi";
import { baasProxy } from "./baasConsoleClient";
import { listPixPayments } from "./baasPixApi";

export const BAAS_TRANSACTION_TYPES = ["SWIFT", "ACH", "WIRE", "SEPA", "PIX", "TED"];
export const BAAS_TRANSACTION_STATUSES = ["processed", "pending", "rejected"];

const MOCK_CLIENTS = [
  { id: "cli-1", name: "Tech Solutions LTDA" },
  { id: "cli-2", name: "João Pedro Santos" },
  { id: "cli-3", name: "Global Pay Corp" },
  { id: "cli-4", name: "Maria Oliveira" },
  { id: "cli-5", name: "Comércio Rápido ME" },
];

const buildPld = (overrides = {}) => ({
  riskScore: 25,
  riskLevel: "low",
  screeningResult: "clear",
  pepCheck: "not_listed",
  sanctionsCheck: "clear",
  flags: [],
  analystNotes: "",
  reviewedAt: null,
  ...overrides,
});

const buildGenericMetadata = (overrides = {}) => ({
  external_id: "",
  internal_id: "",
  correlation_id: "",
  provider_ref: "",
  ...overrides,
});

const METADATA_FIELD_ORDER = {
  common: [
    "external_id",
    "internal_id",
    "operation_id",
    "correlation_id",
    "provider_ref",
    "counterparty_name",
    "counterparty_document",
  ],
  PIX: [
    "e2e_id",
    "txid",
    "pix_key",
    "pix_key_type",
    "qr_code_id",
    "initiation_type",
    "bacen_message_id",
    "payer_name",
    "payer_document",
    "payer_bank",
    "payer_ispb",
    "payer_branch",
    "payer_account",
    "payer_account_type",
    "receiver_name",
    "receiver_document",
    "receiver_bank",
    "receiver_ispb",
    "receiver_branch",
    "receiver_account",
    "receiver_account_type",
    "extra_field",
    "batch_id",
    "batch_status",
  ],
  TED: [
    "ted_control_number",
    "favored_name",
    "destination_bank_ispb",
    "destination_agency",
    "destination_account",
    "ted_type",
  ],
  INTERNAL_TRANSFER: [
    "origin_account_id",
    "origin_account_name",
    "origin_document",
    "origin_account",
    "destination_account_id",
    "destination_account_name",
    "destination_document",
    "destination_account",
  ],
  SWIFT: ["swift_reference", "uetr", "bic_sender", "bic_receiver", "mt_message_id"],
  ACH: ["ach_trace_number", "sec_code", "odfi_routing", "rdfi_routing"],
  WIRE: ["wire_reference", "fed_reference", "imad", "omad"],
  SEPA: ["sepa_end_to_end_id", "creditor_iban", "creditor_bic", "mandate_id"],
};

const DOCUMENT_METADATA_KEY = /document|tax_id|cpf|cnpj|national_registration/i;

/** Masks CPF (11) / CNPJ (14). Already-masked values are kept. */
export const maskBaasTaxId = (value) => {
  if (value == null || value === "") return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if (trimmed.includes("*")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 11) {
    return `***.${digits.slice(3, 6)}.${digits.slice(6, 9)}-**`;
  }
  if (digits.length === 14) {
    return `**.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-**`;
  }
  return "";
};

const maskMetadataValue = (key, value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  const text = String(value).trim();
  if (!text) return "";
  if (!DOCUMENT_METADATA_KEY.test(key) && key !== "pix_key") return text;
  if (key === "pix_key") {
    const maskedKey = maskBaasTaxId(text);
    return maskedKey || text;
  }
  return maskBaasTaxId(text) || (text.includes("*") ? text : "");
};

export const getBaasTransactionMetadataLabelKey = (fieldKey) =>
  `baas.transactions.metadata.fields.${fieldKey}`;

export const getBaasTransactionMetadataEntries = (transaction) => {
  if (!transaction?.metadata) return [];

  const type = transaction.type || transaction.rail;
  const orderedKeys = [
    ...METADATA_FIELD_ORDER.common,
    ...(METADATA_FIELD_ORDER[type] ?? []),
  ];
  const seen = new Set();
  const entries = [];

  const pushEntry = (key) => {
    if (seen.has(key)) return;
    const value = maskMetadataValue(key, transaction.metadata[key]);
    if (value == null || value === "") return;
    seen.add(key);
    entries.push({ key, value });
  };

  orderedKeys.forEach(pushEntry);
  Object.keys(transaction.metadata).forEach(pushEntry);

  return entries;
};

const MOCK_TRANSACTIONS = {
  "valut-hml": [
    {
      id: "txn-001",
      referenceId: "TXN-20260701-001",
      type: "PIX",
      status: "processed",
      clientId: "cli-1",
      clientName: "Tech Solutions LTDA",
      counterpartyName: "Fornecedor ABC Ltda",
      counterpartyDocument: "11.222.333/0001-55",
      amount: 1500000,
      currency: "BRL",
      fee: 0,
      direction: "outbound",
      createdAt: "2026-07-01T14:22:00.000Z",
      processedAt: "2026-07-01T14:22:03.000Z",
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({ riskScore: 18, riskLevel: "low" }),
      metadata: buildGenericMetadata({
        external_id: "EXT-PIX-20260701-88421",
        internal_id: "INT-txn-001-valut-hml",
        correlation_id: "corr-7f3a9c12-b4e5-6789-abcd-ef0123456789",
        provider_ref: "STARK-PIX-9912044",
        e2e_id: "E18236120260701142203s08742a1b2c3d4e5",
        txid: "TXN202607011422001ABC",
        pix_key: "11.222.333/0001-55",
        qr_code_id: "QR-PIX-DYNAMIC-778812",
        initiation_type: "DICT",
        bacen_message_id: "M00000001234567890123456789012345",
      }),
    },
    {
      id: "txn-002",
      referenceId: "TXN-20260702-002",
      type: "TED",
      status: "pending",
      clientId: "cli-2",
      clientName: "João Pedro Santos",
      counterpartyName: "Banco Destino S.A.",
      counterpartyDocument: "000.000.000/0001-01",
      amount: 850000,
      currency: "BRL",
      fee: 1090,
      direction: "outbound",
      createdAt: "2026-07-02T09:10:00.000Z",
      processedAt: null,
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({
        riskScore: 42,
        riskLevel: "medium",
        screeningResult: "review",
        flags: ["high_amount"],
        analystNotes: "Aguardando revisão manual — valor acima do limite diário.",
      }),
      metadata: buildGenericMetadata({
        external_id: "EXT-TED-20260702-55210",
        internal_id: "INT-txn-002-valut-hml",
        correlation_id: "corr-a1b2c3d4-e5f6-7890-abcd-ef1234567890",
        provider_ref: "CELCOIN-TED-4401982",
        ted_control_number: "STR00087654321098765432",
        destination_bank_ispb: "00000000",
        destination_agency: "1234",
        destination_account: "987654-3",
      }),
    },
    {
      id: "txn-003",
      referenceId: "TXN-20260703-003",
      type: "SWIFT",
      status: "processed",
      clientId: "cli-1",
      clientName: "Tech Solutions LTDA",
      counterpartyName: "Acme Corp International",
      counterpartyDocument: "US-8829100",
      amount: 2500000,
      currency: "USD",
      fee: 3500,
      direction: "outbound",
      createdAt: "2026-07-03T11:30:00.000Z",
      processedAt: "2026-07-03T16:45:00.000Z",
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({ riskScore: 55, riskLevel: "medium", flags: ["cross_border"] }),
      metadata: buildGenericMetadata({
        external_id: "EXT-SWIFT-20260703-33102",
        internal_id: "INT-txn-003-valut-hml",
        correlation_id: "corr-swift-9d8e7f6a-5b4c-3210-fedc-ba9876543210",
        provider_ref: "JPM-SWIFT-7721901",
        swift_reference: "JPMGUS33XXX-20260703-001",
        uetr: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
        bic_sender: "VALUBRSPXXX",
        bic_receiver: "CHASUS33XXX",
        mt_message_id: "MT103-20260703-884412",
      }),
    },
    {
      id: "txn-004",
      referenceId: "TXN-20260704-004",
      type: "ACH",
      status: "rejected",
      clientId: "cli-4",
      clientName: "Maria Oliveira",
      counterpartyName: "Payroll Services Inc",
      counterpartyDocument: "US-4412001",
      amount: 320000,
      currency: "USD",
      fee: 0,
      direction: "inbound",
      createdAt: "2026-07-04T08:00:00.000Z",
      processedAt: null,
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({
        riskScore: 88,
        riskLevel: "high",
        screeningResult: "blocked",
        sanctionsCheck: "match",
        flags: ["sanctions_match", "blocked"],
        analystNotes: "Transação recusada — contraparte em lista de sanções.",
        reviewedAt: "2026-07-04T08:05:00.000Z",
      }),
      metadata: buildGenericMetadata({
        external_id: "EXT-ACH-20260704-11890",
        internal_id: "INT-txn-004-valut-hml",
        correlation_id: "corr-ach-11223344-5566-7788-99aa-bbccddeeff00",
        provider_ref: "COLUMN-ACH-3301987",
        ach_trace_number: "20260704120000123456",
        sec_code: "PPD",
        odfi_routing: "021000021",
        rdfi_routing: "121000248",
      }),
    },
    {
      id: "txn-005",
      referenceId: "TXN-20260705-005",
      type: "WIRE",
      status: "processed",
      clientId: "cli-3",
      clientName: "Global Pay Corp",
      counterpartyName: "European Trading GmbH",
      counterpartyDocument: "DE-9988776",
      amount: 5000000,
      currency: "EUR",
      fee: 4500,
      direction: "outbound",
      createdAt: "2026-07-05T10:15:00.000Z",
      processedAt: "2026-07-05T10:20:00.000Z",
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({ riskScore: 30, riskLevel: "low" }),
      metadata: buildGenericMetadata({
        external_id: "EXT-WIRE-20260705-90221",
        internal_id: "INT-txn-005-valut-hml",
        correlation_id: "corr-wire-44556677-8899-aabb-ccdd-eeff00112233",
        provider_ref: "DEUTSCHE-WIRE-5510234",
        wire_reference: "WIRE-DE-20260705-0099",
        fed_reference: "20260705MMQFMP2L000123",
        imad: "20260705B1Q82R01234567",
        omad: "20260705MMQFMP2L000123",
      }),
    },
    {
      id: "txn-006",
      referenceId: "TXN-20260706-006",
      type: "SEPA",
      status: "pending",
      clientId: "cli-3",
      clientName: "Global Pay Corp",
      counterpartyName: "Nordic Supplies AB",
      counterpartyDocument: "SE-5544332",
      amount: 1200000,
      currency: "EUR",
      fee: 250,
      direction: "outbound",
      createdAt: "2026-07-06T13:40:00.000Z",
      processedAt: null,
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({
        riskScore: 35,
        riskLevel: "low",
        screeningResult: "review",
        flags: ["new_counterparty"],
      }),
      metadata: buildGenericMetadata({
        external_id: "EXT-SEPA-20260706-44102",
        internal_id: "INT-txn-006-valut-hml",
        correlation_id: "corr-sepa-99887766-5544-3322-1100-aabbccddeeff",
        provider_ref: "BANKINGCIRCLE-SEPA-2201981",
        sepa_end_to_end_id: "NOTPROVIDED-20260706-SEPA-006",
        creditor_iban: "SE3550000000054910000003",
        creditor_bic: "ESSESESSXXX",
        mandate_id: "MNDT-SEPA-2026-00644",
      }),
    },
    {
      id: "txn-007",
      referenceId: "TXN-20260707-007",
      type: "PIX",
      status: "processed",
      clientId: "cli-5",
      clientName: "Comércio Rápido ME",
      counterpartyName: "Distribuidora Norte",
      counterpartyDocument: "33.444.555/0001-66",
      amount: 45000,
      currency: "BRL",
      fee: 0,
      direction: "inbound",
      createdAt: "2026-07-07T16:00:00.000Z",
      processedAt: "2026-07-07T16:00:01.000Z",
      refunded: true,
      instanceId: "valut-hml",
      pld: buildPld({ riskScore: 12, riskLevel: "low" }),
      metadata: buildGenericMetadata({
        external_id: "EXT-PIX-20260707-22018",
        internal_id: "INT-txn-007-valut-hml",
        correlation_id: "corr-pix-refund-66778899-aabb-ccdd-eeff00112233",
        provider_ref: "STARK-PIX-9912088",
        e2e_id: "E18236120260707160001r55443f6a7b8c9d0e1",
        txid: "TXN202607071600007REF",
        pix_key: "distribuidora@norte.com.br",
        qr_code_id: "QR-PIX-STATIC-334455",
        initiation_type: "QR_CODE",
        bacen_message_id: "M00000009876543210987654321098765",
      }),
    },
    {
      id: "txn-008",
      referenceId: "TXN-20260708-008",
      type: "TED",
      status: "rejected",
      clientId: "cli-2",
      clientName: "João Pedro Santos",
      counterpartyName: "Conta inválida",
      counterpartyDocument: "000.000.000-00",
      amount: 100000,
      currency: "BRL",
      fee: 0,
      direction: "outbound",
      createdAt: "2026-07-08T07:30:00.000Z",
      processedAt: null,
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({
        riskScore: 72,
        riskLevel: "high",
        screeningResult: "blocked",
        flags: ["invalid_account"],
        analystNotes: "Conta de destino inválida — transação recusada pelo banco.",
      }),
      metadata: buildGenericMetadata({
        external_id: "EXT-TED-20260708-00991",
        internal_id: "INT-txn-008-valut-hml",
        correlation_id: "corr-ted-reject-22334455-6677-8899-aabbccddeeff",
        provider_ref: "CELCOIN-TED-4402099",
        ted_control_number: "STR00011223344556677889",
        destination_bank_ispb: "60701190",
        destination_agency: "0001",
        destination_account: "000000-0",
      }),
    },
    {
      id: "txn-009",
      referenceId: "TXN-20260709-009",
      type: "PIX",
      status: "processed",
      clientId: "cli-1",
      clientName: "Tech Solutions LTDA",
      counterpartyName: "Serviços Cloud BR",
      counterpartyDocument: "44.555.666/0001-77",
      amount: 299900,
      currency: "BRL",
      fee: 0,
      direction: "outbound",
      createdAt: "2026-07-09T12:00:00.000Z",
      processedAt: "2026-07-09T12:00:02.000Z",
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({ riskScore: 22, riskLevel: "low" }),
      metadata: buildGenericMetadata({
        external_id: "EXT-PIX-20260709-77812",
        internal_id: "INT-txn-009-valut-hml",
        correlation_id: "corr-pix-out-33445566-7788-99aa-bbccddeeff00",
        provider_ref: "STARK-PIX-9912155",
        e2e_id: "E18236120260709120002o99887a1b2c3d4e5f6",
        txid: "TXN202607091200009CLOUD",
        pix_key: "+5511987654321",
        qr_code_id: "QR-PIX-DYNAMIC-990011",
        initiation_type: "MANUAL",
        bacen_message_id: "M00000005556667778889990001112222",
      }),
    },
    {
      id: "txn-010",
      referenceId: "TXN-20260709-010",
      type: "SWIFT",
      status: "pending",
      clientId: "cli-3",
      clientName: "Global Pay Corp",
      counterpartyName: "Asia Pacific Ltd",
      counterpartyDocument: "HK-7788990",
      amount: 8000000,
      currency: "USD",
      fee: 5500,
      direction: "inbound",
      createdAt: "2026-07-09T15:30:00.000Z",
      processedAt: null,
      refunded: false,
      instanceId: "valut-hml",
      pld: buildPld({
        riskScore: 60,
        riskLevel: "medium",
        screeningResult: "review",
        pepCheck: "potential_match",
        flags: ["pep_review", "high_amount"],
        analystNotes: "Possível match PEP — análise em andamento.",
      }),
      metadata: buildGenericMetadata({
        external_id: "EXT-SWIFT-20260709-55001",
        internal_id: "INT-txn-010-valut-hml",
        correlation_id: "corr-swift-pending-55667788-99aa-bbcc-ddeeff001122",
        provider_ref: "CITI-SWIFT-8819022",
        swift_reference: "CITIHKHX-20260709-010",
        uetr: "b2c3d4e5-f6a7-8901-bcde-f23456789012",
        bic_sender: "VALUBRSPXXX",
        bic_receiver: "CITIHKHX",
        mt_message_id: "MT103-20260709-990011",
      }),
    },
  ],
  valut: [
    {
      id: "txn-102",
      referenceId: "TXN-20260702-102",
      type: "ACH",
      status: "pending",
      clientId: "cli-3",
      clientName: "Global Pay Corp",
      counterpartyName: "Pacific Imports LLC",
      counterpartyDocument: "US-3300441",
      amount: 1750000,
      currency: "USD",
      fee: 1200,
      direction: "inbound",
      createdAt: "2026-07-02T11:20:00.000Z",
      processedAt: null,
      refunded: false,
      instanceId: "valut",
      pld: buildPld({
        riskScore: 48,
        riskLevel: "medium",
        screeningResult: "review",
        flags: ["new_counterparty"],
      }),
      metadata: buildGenericMetadata({
        external_id: "EXT-ACH-20260702-10201",
        internal_id: "INT-txn-102-valut",
        correlation_id: "corr-ach-valut-11223344-5566-7788-99aa-bbccddeeff",
        provider_ref: "COLUMN-ACH-4405501",
        ach_trace_number: "20260702112000998877",
        sec_code: "CCD",
        odfi_routing: "021000021",
        rdfi_routing: "026009593",
      }),
    },
    {
      id: "txn-101",
      referenceId: "TXN-20260701-101",
      type: "WIRE",
      status: "processed",
      clientId: "cli-3",
      clientName: "Global Pay Corp",
      counterpartyName: "US Treasury Partner",
      counterpartyDocument: "US-1100220",
      amount: 10000000,
      currency: "USD",
      fee: 7500,
      direction: "outbound",
      createdAt: "2026-07-01T10:00:00.000Z",
      processedAt: "2026-07-01T10:05:00.000Z",
      refunded: false,
      instanceId: "valut",
      pld: buildPld({ riskScore: 20, riskLevel: "low" }),
      metadata: buildGenericMetadata({
        external_id: "EXT-WIRE-20260701-10101",
        internal_id: "INT-txn-101-valut",
        correlation_id: "corr-wire-valut-99887766-5544-3322-1100-aabbccddeeff",
        provider_ref: "JPM-WIRE-7721001",
        wire_reference: "WIRE-US-20260701-101",
        fed_reference: "20260701MMQFMP2L000456",
        imad: "20260701B1Q82R09876543",
        omad: "20260701MMQFMP2L000456",
      }),
    },
  ],
};

export const getBaasTransactionClients = (instanceIdOrTransactions) => {
  const transactions = Array.isArray(instanceIdOrTransactions)
    ? instanceIdOrTransactions
    : getMockBaasTransactions(instanceIdOrTransactions);
  const clientMap = new Map();
  transactions.forEach((txn) => {
    const id = txn.entityId || txn.clientId;
    const name = txn.entityName || txn.clientName;
    if (id && name) {
      clientMap.set(id, { id, name });
    }
  });
  return [...clientMap.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/** Alias used by entity filter panels (same source as clients). */
export const getBaasTransactionEntities = getBaasTransactionClients;

const DIRECTION_TOKENS = new Set([
  "CREDIT",
  "DEBIT",
  "INBOUND",
  "OUTBOUND",
  "C",
  "D",
  "LEDGER",
]);

/**
 * Resolve payment rail/type badge from statement fields.
 * Never uses debit/credit `operation` and never defaults unknown rows to TED.
 */
export const resolveBaasTransactionRail = (item = {}) => {
  const categoryName = item?.category?.name || item?.category?.Name || "";
  const description = item?.description || item?.Description || "";
  const endToEnd =
    item?.end_to_end_id || item?.endToEndId || item?.EndToEndId || item?.trace_id || "";

  const candidates = [
    item?.rail,
    item?.payment_rail,
    item?.paymentRail,
    item?.transaction_type,
    item?.transactionType,
    item?.type,
    item?.Type,
    categoryName,
  ];

  for (const candidate of candidates) {
    const upper = String(candidate || "")
      .trim()
      .toUpperCase();
    if (!upper || DIRECTION_TOKENS.has(upper)) continue;
    if (BAAS_TRANSACTION_TYPES.includes(upper)) return upper;
  }

  const haystack = [...candidates, description, endToEnd]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

  // PIX before TED — descriptions often look like "PIX - E34747…".
  if (
    /\bPIX\b/.test(haystack) ||
    /QR\s*CODE|QRCODE/.test(haystack) ||
    /^E[0-9A-Z]{20,}/i.test(String(endToEnd || "").trim())
  ) {
    return "PIX";
  }

  for (const rail of ["SWIFT", "ACH", "WIRE", "SEPA"]) {
    if (haystack.includes(rail)) return rail;
  }

  // Explicit TED only (word boundary) — never a silent default.
  if (/\bTED\b/.test(haystack)) return "TED";

  if (
    /\bINTERNAL_TRANSFER\b/.test(haystack) ||
    /\bTRANSFER[EÊ]NCIA\b/.test(haystack) ||
    /^itr_/i.test(String(endToEnd || item?.operation_id || "").trim())
  ) {
    return "INTERNAL_TRANSFER";
  }

  return "";
};

const resolveStatementDirection = (item, amountMinor) => {
  const explicit = String(item?.direction || item?.operation || "").toLowerCase();
  if (explicit === "outbound" || explicit === "debit" || explicit === "d") {
    return "outbound";
  }
  if (explicit === "inbound" || explicit === "credit" || explicit === "c") {
    return "inbound";
  }
  if (Number(amountMinor) < 0) return "outbound";
  return "inbound";
};

/**
 * Pix payment amounts may arrive as major units (decimal) or cents (integer).
 * BaaS statement `amount` is always signed cents — use Math.abs directly there.
 */
const toAmountCents = (raw) => {
  if (raw == null || raw === "") return 0;
  const numeric = Math.abs(Number(raw));
  if (!Number.isFinite(numeric)) return 0;
  if (Number.isInteger(Number(raw)) || numeric >= 1000) {
    return Math.round(numeric);
  }
  return Math.round(numeric * 100);
};

const formatStatementDateParam = (value) => {
  if (!value) return undefined;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Map BaaS `AccountStatementItemResponse` onto the Transações / Extrato UI shape.
 * Amounts stay in cents (Carteira `formatCurrency` convention).
 */
export const mapStatementToUi = (item, account = {}) => {
  // BaaS: signed minor units (cents). Prefer `amount`; `operation_amount` is the
  // gross operation figure and must not replace ledger amount when both exist.
  const amountMinor = Number(item?.amount ?? 0);
  const amountCents = Math.abs(Number.isFinite(amountMinor) ? amountMinor : 0);
  const feeCents = Math.abs(Number(item?.fee_amount ?? 0));
  const rail = resolveBaasTransactionRail(item);
  const title = item?.description || item?.name || "";
  const subtitle =
    item?.name && item?.name !== item?.description ? item.name : "";
  const entityId = account.entityId || account.id || item?.account_id || "";
  const entityName =
    item?.entity_name || account.entityName || account.alias || account.id || "";
  const instanceId =
    item?.instance_id ??
    item?.instanceId ??
    account.baasInstanceId ??
    account.instanceId ??
    null;
  const instanceName = item?.instance_name || item?.instanceName || account.instanceName || "";
  const statusRaw = String(item?.status || "processed").toLowerCase();
  const status =
    statusRaw.includes("pend")
      ? "pending"
      : statusRaw.includes("reject") || statusRaw.includes("fail")
        ? "rejected"
        : "processed";

  return {
    id: item?.id || `${account.id || "acct"}-${item?.created_at || Date.now()}`,
    referenceId:
      item?.end_to_end_id ||
      item?.operation_id ||
      item?.external_id ||
      item?.reference ||
      item?.id ||
      "—",
    type: rail || "—",
    rail,
    status,
    entityId,
    entityName,
    clientId: entityId,
    clientName: entityName,
    description: title || "—",
    name: subtitle || undefined,
    counterpartyName:
      item?.counterparty_name || title || "—",
    counterpartyDocument: maskBaasTaxId(
      item?.counterparty_document || item?.metadata?.counterparty_document || "",
    ),
    amount: amountCents,
    currency: String(
      item?.currency_code || item?.currency || account.currencyCode || "BRL",
    ).toUpperCase(),
    fee: feeCents,
    direction: resolveStatementDirection(item, amountMinor),
    createdAt: item?.created_at || item?.date || null,
    processedAt: item?.processed_at || item?.created_at || null,
    refunded: Boolean(item?.refunded || item?.is_refund),
    instanceId,
    instanceName: instanceName || undefined,
    accountId: account.id || item?.account_id || "",
    category: item?.category || null,
    balanceBefore:
      item?.balance_before != null ? Math.abs(Number(item.balance_before)) : undefined,
    balanceAfter:
      item?.balance_after != null ? Math.abs(Number(item.balance_after)) : undefined,
    pld: buildPld(),
    metadata: buildStatementMetadata(item),
    raw: item,
  };
};

/** Copy sibling `PIX_FEE` / Tarifa PIX amount onto the PIX debit when fee_amount is 0. */
export const attachPixFeeToStatementRows = (rows = []) => {
  const feeByParentOp = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const type = String(row?.raw?.type || row?.type || "");
    const description = String(row?.description || "");
    const isFeeRow =
      /^PIX_FEE$/i.test(type) || /^Tarifa PIX\b/i.test(description);
    if (!isFeeRow) return;
    const op = String(
      row?.raw?.operation_id || row?.metadata?.operation_id || "",
    ).trim();
    const parentOp = op.replace(/:pix-fee(?::cust)?$/i, "");
    const cents = Math.abs(Number(row?.fee || row?.amount) || 0);
    if (parentOp && cents > 0) feeByParentOp.set(parentOp, cents);
  });

  return (Array.isArray(rows) ? rows : []).map((row) => {
    if (Math.abs(Number(row?.fee) || 0) > 0) return row;
    const op = String(
      row?.raw?.operation_id || row?.metadata?.operation_id || "",
    ).trim();
    const fee = feeByParentOp.get(op);
    if (!fee) return row;
    return { ...row, fee };
  });
};

const pixEndToEndId = (value) => {
  const text = String(value || "").trim();
  if (!text || /^itr_/i.test(text)) return "";
  return /^[EDed]/.test(text) ? text : "";
};

const isEmptyMetadataValue = (value) =>
  value == null || value === "" || (typeof value === "object" && !Object.keys(value).length);

const buildStatementMetadata = (item = {}) => {
  const fromApi =
    item?.metadata && typeof item.metadata === "object" && !Array.isArray(item.metadata)
      ? item.metadata
      : {};
  const merged = buildGenericMetadata({
    external_id: fromApi.external_id || item?.external_id || "",
    internal_id: fromApi.internal_id || item?.id || "",
    correlation_id: fromApi.correlation_id || item?.correlation_id || "",
    provider_ref: fromApi.provider_ref || item?.provider_ref || "",
    e2e_id: fromApi.e2e_id || pixEndToEndId(item?.end_to_end_id) || "",
    operation_id: fromApi.operation_id || item?.operation_id || "",
    ...fromApi,
  });

  if (item?.counterparty_name && !merged.counterparty_name) {
    merged.counterparty_name = item.counterparty_name;
  }
  if (item?.counterparty_document && !merged.counterparty_document) {
    merged.counterparty_document = item.counterparty_document;
  }
  if (item?.batch_id && !merged.batch_id) {
    merged.batch_id = item.batch_id;
  }
  if (item?.batch_status && !merged.batch_status) {
    merged.batch_status = item.batch_status;
  }

  const metadata = {};
  Object.entries(merged).forEach(([key, value]) => {
    if (isEmptyMetadataValue(value)) return;
    const masked = maskMetadataValue(key, value);
    if (masked) metadata[key] = masked;
  });
  return metadata;
};

const mapPixPaymentToUi = (payment, account) => {
  const entityId = account.entityId || account.id;
  const entityName = account.entityName || account.alias || account.id;
  const receiver = payment?.receiver || {};
  const description =
    payment?.counterparty_name ||
    payment?.destination_name ||
    payment?.receiver_name ||
    receiver.name ||
    payment?.key ||
    payment?.external_id ||
    "PIX";
  const document =
    payment?.counterparty_document ||
    receiver.document ||
    payment?.receiver_document ||
    "";

  return {
    id: payment?.payment_id || payment?.id,
    referenceId: payment?.external_id || payment?.payment_id || "—",
    type: "PIX",
    rail: "PIX",
    status: String(payment?.status || "processed").toLowerCase().includes("pend")
      ? "pending"
      : String(payment?.status || "").toLowerCase().includes("fail")
        ? "rejected"
        : "processed",
    entityId,
    entityName,
    clientId: entityId,
    clientName: entityName,
    description,
    counterpartyName: description,
    counterpartyDocument: maskBaasTaxId(document),
    amount: toAmountCents(payment?.amount),
    currency: "BRL",
    fee: 0,
    direction: "outbound",
    createdAt: payment?.created_at || null,
    processedAt: payment?.created_at || null,
    refunded: false,
    instanceId: account.instanceId,
    accountId: account.id,
    pld: buildPld(),
    metadata: buildStatementMetadata({
      ...payment,
      metadata: {
        external_id: payment?.external_id || "",
        internal_id: payment?.payment_id || payment?.id || "",
        e2e_id: payment?.end_to_end_id || payment?.end_to_end || receiver.end_to_end_id || "",
        pix_key: payment?.key || receiver.key || "",
        pix_key_type: receiver.key_type || "",
        receiver_name: payment?.receiver_name || receiver.name || "",
        receiver_document: receiver.document || "",
        receiver_bank: receiver.bank_code || "",
        receiver_branch: receiver.branch || "",
        receiver_account: receiver.account_number || "",
        receiver_account_type: receiver.account_type || "",
        payer_name: payment?.payer_name || "",
        ...(payment?.metadata && typeof payment.metadata === "object" ? payment.metadata : {}),
      },
      counterparty_name: description,
      counterparty_document: document,
    }),
    raw: payment,
  };
};

/**
 * Valut matrix only: GET `v1/accounts/peer-statements` — ledger movements of every
 * active instance (including PROGRAM_RESERVE / Root shells). Empty for partners.
 */
export const fetchBaasPeerStatements = async (instance, options = {}) => {
  if (!instance || !isValutSegregationWalletInstance(instance)) return [];

  const startDate = formatStatementDateParam(options.startDate);
  const endDate = formatStatementDateParam(options.endDate);
  const { normalized, wantsAll } = normalizeBaasStatementsQuery({
    fetch_all: options.fetchAll !== false,
    page: 1,
    page_size: options.pageSize ?? BAAS_STATEMENT_PAGE_SIZE,
    startDate,
    endDate,
    currencyCode: options.currencyCode,
    instanceId: options.instanceId,
  });
  if (options.instanceId != null && Number.isFinite(Number(options.instanceId))) {
    normalized.instance_id = Number(options.instanceId);
  }

  const baasPath = "v1/accounts/peer-statements";
  const loadPage = (page) =>
    baasProxy({
      instance,
      method: "GET",
      baasPath,
      query: { ...normalized, page, page_size: normalized.page_size },
    });

  let payload;
  if (!wantsAll) {
    payload = await loadPage(normalized.page || 1);
  } else {
    const maxItems = Math.min(
      MAX_BAAS_STATEMENT_ITEMS,
      Math.max(1, Number(options.max_items ?? options.maxItems) || MAX_BAAS_STATEMENT_ITEMS),
    );
    const pageSize = normalized.page_size;
    const items = [];
    let total = Infinity;
    let page = 1;
    const maxPages = Math.ceil(maxItems / pageSize);
    while (page <= maxPages && items.length < total && items.length < maxItems) {
      const data = await loadPage(page);
      const batch = Array.isArray(data?.items) ? data.items : [];
      total = Number(data?.total ?? batch.length);
      items.push(...batch);
      payload = { ...(data || {}), items, total };
      if (batch.length < pageSize) break;
      page += 1;
    }
    if (payload?.items) payload.items = items.slice(0, maxItems);
  }

  const items = Array.isArray(payload?.items) ? payload.items : [];
  return attachPixFeeToStatementRows(items.map((item) => mapStatementToUi(item, {})));
};

/**
 * Valut matrix only: GET `v1/accounts/peer-pending-pix` — MD06 / PENDING_RETRY queue.
 * Empty for partners.
 */
export const fetchBaasPeerPendingPix = async (instance, options = {}) => {
  if (!instance || !isValutSegregationWalletInstance(instance)) return [];

  const query = {};
  if (options.instanceId != null && Number.isFinite(Number(options.instanceId))) {
    query.instance_id = Number(options.instanceId);
  }

  const payload = await baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/accounts/peer-pending-pix",
    query,
  });

  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => mapPendingPixToUi(item));
};

export const mapPendingPixToUi = (item = {}) => {
  const amountCents = Math.abs(Number(item?.amount ?? 0));
  const statusRaw = String(item?.status || "PENDING_RETRY").toUpperCase();
  const pendingKind = item?.pending_kind || item?.pendingKind || "pending_retry";
  const description =
    item?.description ||
    item?.reason_info ||
    "PIX estorno pendente (MD06)";

  return {
    id: item?.id || `pending-pix-${Date.now()}`,
    referenceId: item?.external_id || item?.id || "—",
    type: "PIX",
    rail: "PIX",
    status: "pending",
    pendingPix: true,
    pendingKind,
    pendingStatus: statusRaw,
    entityId: item?.account_id || "",
    entityName: item?.entity_name || "",
    clientId: item?.account_id || "",
    clientName: item?.entity_name || "",
    description,
    counterpartyName: description,
    counterpartyDocument: "",
    amount: amountCents,
    currency: String(item?.currency_code || "BRL").toUpperCase(),
    fee: 0,
    direction: "inbound",
    createdAt: item?.created_at || null,
    processedAt: null,
    refunded: false,
    instanceId: item?.instance_id ?? null,
    instanceName: item?.instance_name || undefined,
    accountId: item?.account_id || "",
    pld: buildPld({ riskLevel: "medium", screeningResult: "review" }),
    metadata: {
      external_id: item?.external_id || "",
      reason_code: item?.reason_code || "",
      reason_info: item?.reason_info || "",
      rejection_reason: item?.rejection_reason || "",
      pending_kind: pendingKind,
      pending_status: statusRaw,
    },
    raw: item,
  };
};

const mergeCurrentInstanceShellAccounts = async (instance, accounts = []) => {
  const byId = new Map();
  const instanceId = instance?.baasInstanceId ?? instance?.instanceId ?? null;
  const instanceName = instance?.displayName || instance?.name || "";
  const add = (row) => {
    const id = String(row?.id || "").trim();
    if (!id || byId.has(id)) return;
    byId.set(id, {
      ...row,
      id,
      entityId: row.entityId || id,
      entityName: row.entityName || row.name || row.alias || id,
      instanceId,
      instanceName,
    });
  };
  (accounts || []).forEach(add);
  if (instance?.rootAccountId) {
    add({
      id: String(instance.rootAccountId).trim(),
      name: "Root",
      entityName: "Root",
    });
  }
  try {
    const options = await fetchInternalTransferOptions(instance);
    [...(options.sourceAccounts || []), ...(options.destinationAccounts || []), ...(options.virtualAccounts || [])]
      .filter(Boolean)
      .forEach((row) =>
        add({
          id: row.id,
          name: row.name,
          entityName: row.name || row.role,
          role: row.role,
        }),
      );
  } catch {
    // Shells stay optional — customer accounts still load.
  }
  return [...byId.values()];
};

/**
 * Loads ledger statements across instance accounts (BaaS GET …/statements).
 * Valut matrix uses peer-statements (all instances). Partners stay instance-scoped.
 * Optionally merges recent Pix payments when `includePix` is explicitly true.
 *
 * @param {object} instance
 * @param {{
 *   accounts?: array,
 *   accountIds?: string[],
 *   startDate?: Date|string,
 *   endDate?: Date|string,
 *   currencyCode?: string,
 *   fetchAll?: boolean,
 *   pageSize?: number,
 *   includePix?: boolean,
 * }} [options]
 */
export const fetchBaasTransactions = async (instance, options = {}) => {
  if (!instance) return [];

  const isAccountScoped = Boolean(options.accountIds?.length || options.accounts);
  if (!isAccountScoped && isValutSegregationWalletInstance(instance)) {
    try {
      const [statements, pendingPix] = await Promise.all([
        fetchBaasPeerStatements(instance, options),
        options.includePendingPix === false
          ? Promise.resolve([])
          : fetchBaasPeerPendingPix(instance, options).catch(() => []),
      ]);
      const byId = new Map();
      [...pendingPix, ...statements].forEach((row) => {
        const id = String(row?.id || "").trim();
        if (!id || byId.has(id)) return;
        byId.set(id, row);
      });
      return [...byId.values()].sort((a, b) => {
        const ta = new Date(a?.createdAt || 0).getTime();
        const tb = new Date(b?.createdAt || 0).getTime();
        return tb - ta;
      });
    } catch (error) {
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[baas] GET /v1/accounts/peer-statements failed", error);
      }
    }
  }

  let accounts = options.accounts;
  if (!accounts) {
    if (options.accountIds?.length) {
      // Account-scoped callers (Extrato) — avoid listing every entity account.
      accounts = options.accountIds.map((id) => ({
        id,
        entityId: id,
        entityName: "",
        alias: id,
        instanceId: instance?.baasInstanceId ?? instance?.id,
        instanceName: instance?.displayName || instance?.name,
      }));
    } else {
      accounts = await fetchBaasAccounts(instance).catch(() => []);
    }
  } else if (options.accountIds?.length) {
    const allowed = new Set(options.accountIds.map(String));
    accounts = accounts.filter((account) => allowed.has(String(account.id)));
  }
  if (!isAccountScoped) {
    accounts = await mergeCurrentInstanceShellAccounts(instance, accounts);
  }
  if (!accounts.length) return [];

  const startDate = formatStatementDateParam(options.startDate);
  const endDate = formatStatementDateParam(options.endDate);
  const fetchAll = options.fetchAll !== false;
  const statementQuery = {
    fetch_all: fetchAll,
    page: 1,
    page_size: options.pageSize ?? BAAS_STATEMENT_PAGE_SIZE,
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate ? { end_date: endDate } : {}),
    ...(options.currencyCode
      ? { currency_code: String(options.currencyCode).toUpperCase() }
      : {}),
  };

  const statementBatches = await Promise.all(
    accounts.map(async (account) => {
      try {
        const result = await fetchAccountStatements(
          instance,
          account.id,
          statementQuery,
        );
        return attachPixFeeToStatementRows(
          (result.items || []).map((item) => mapStatementToUi(item, account)),
        );
      } catch {
        return [];
      }
    }),
  );

  // Statements already include PIX / TED / transfers. Opt-in Pix payment merge
  // only for callers that still need pending payment rows not yet on the ledger.
  const includePix = options.includePix === true;
  const pixBatches = includePix
    ? await Promise.all(
        accounts.map(async (account) => {
          try {
            const result = await listPixPayments(instance, account.id, {
              page: 1,
              page_size: 20,
            });
            return (result.items || []).map((item) => mapPixPaymentToUi(item, account));
          } catch {
            return [];
          }
        }),
      )
    : [];

  const merged = [...statementBatches.flat(), ...pixBatches.flat()];
  const seenIds = new Set();
  const seenE2e = new Set();
  return merged
    .filter((txn) => {
      if (!txn?.id || seenIds.has(txn.id)) return false;
      seenIds.add(txn.id);
      const e2e = txn?.metadata?.e2e_id || txn?.referenceId;
      if (e2e && e2e !== "—" && seenE2e.has(e2e)) return false;
      if (e2e && e2e !== "—") seenE2e.add(e2e);
      return true;
    })
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
};

/** @deprecated Prefer fetchBaasTransactions */
export const getMockBaasTransactions = (instanceId) => {
  if (!instanceId) {
    return Object.values(MOCK_TRANSACTIONS).flat();
  }
  return MOCK_TRANSACTIONS[instanceId] ?? [];
};

export const isBaasTransactionRefundEligible = (transaction) => {
  if (!transaction) return false;
  if (transaction.refunded) return false;
  if (transaction.status !== "processed") return false;
  return ["PIX", "TED"].includes(transaction.type);
};

export const isBaasTransactionPendingActionEligible = (transaction) => {
  if (!transaction) return false;
  return transaction.status === "pending";
};

const findMockBaasTransaction = (transactionId, instanceId) => {
  const list = instanceId
    ? (MOCK_TRANSACTIONS[instanceId] ?? [])
    : Object.values(MOCK_TRANSACTIONS).flat();
  return list.find((item) => item.id === transactionId) ?? null;
};

export const releaseBaasTransaction = (transactionId, instanceId) => {
  const txn = findMockBaasTransaction(transactionId, instanceId);
  if (!txn || !isBaasTransactionPendingActionEligible(txn)) {
    return null;
  }
  txn.status = "processed";
  txn.processedAt = new Date().toISOString();
  if (txn.pld) {
    txn.pld.reviewedAt = new Date().toISOString();
    txn.pld.screeningResult = "clear";
  }
  return { ...txn };
};

export const rejectBaasTransaction = (transactionId, instanceId) => {
  const txn = findMockBaasTransaction(transactionId, instanceId);
  if (!txn || !isBaasTransactionPendingActionEligible(txn)) {
    return null;
  }
  txn.status = "rejected";
  txn.processedAt = null;
  if (txn.pld) {
    txn.pld.reviewedAt = new Date().toISOString();
    txn.pld.screeningResult = "blocked";
  }
  return { ...txn };
};

export const returnBaasTransaction = (transactionId, instanceId) => {
  const txn = findMockBaasTransaction(transactionId, instanceId);
  if (!txn || !isBaasTransactionPendingActionEligible(txn)) {
    return null;
  }
  txn.status = "rejected";
  txn.returned = true;
  txn.returnedAt = new Date().toISOString();
  txn.processedAt = null;
  if (txn.pld) {
    txn.pld.reviewedAt = new Date().toISOString();
    txn.pld.screeningResult = "blocked";
    txn.pld.analystNotes = txn.pld.analystNotes
      ? `${txn.pld.analystNotes} Transação devolvida ao originador.`
      : "Transação devolvida ao originador.";
  }
  return { ...txn };
};

export const refundMockBaasTransaction = (transactionId, instanceId) => {
  const txn = findMockBaasTransaction(transactionId, instanceId);
  if (!txn || !isBaasTransactionRefundEligible(txn)) {
    return null;
  }
  txn.refunded = true;
  txn.refundedAt = new Date().toISOString();
  return { ...txn };
};

const CURRENCY_BY_TYPE = {
  PIX: "BRL",
  TED: "BRL",
  SWIFT: "USD",
  ACH: "USD",
  WIRE: "USD",
  SEPA: "EUR",
};

/**
 * Append a simulated transaction to the mock store (HML / mock only).
 * @param {string} instanceId
 * @param {{ type: string, amount: number, clientId?: string, clientName?: string, status?: string, direction?: string }} payload
 * Amount is in major units (e.g. 150.5); stored as minor units (cents).
 */
export const simulateMockBaasTransaction = (instanceId, payload = {}) => {
  if (!instanceId) return null;

  const type = BAAS_TRANSACTION_TYPES.includes(payload.type) ? payload.type : "PIX";
  const status = BAAS_TRANSACTION_STATUSES.includes(payload.status)
    ? payload.status
    : "pending";
  const direction = payload.direction === "inbound" ? "inbound" : "outbound";
  const majorAmount = Number(payload.amount);
  if (!Number.isFinite(majorAmount) || majorAmount <= 0) return null;

  const amount = Math.round(majorAmount * 100);
  const currency = payload.currency || CURRENCY_BY_TYPE[type] || "BRL";
  const client =
    MOCK_CLIENTS.find((item) => item.id === payload.clientId) ??
    (payload.clientName
      ? { id: payload.clientId || `cli-sim-${Date.now()}`, name: payload.clientName }
      : MOCK_CLIENTS[0]);

  const now = new Date();
  const stamp = now.toISOString().slice(0, 10).replace(/-/g, "");
  const id = `txn-sim-${Date.now()}`;
  const referenceId = `TXN-${stamp}-SIM-${String(Date.now()).slice(-4)}`;

  const txn = {
    id,
    referenceId,
    type,
    status,
    clientId: client.id,
    clientName: client.name,
    counterpartyName: payload.counterpartyName || "Simulated Counterparty",
    counterpartyDocument: payload.counterpartyDocument || "00.000.000/0001-00",
    amount,
    currency,
    fee: 0,
    direction,
    createdAt: now.toISOString(),
    processedAt: status === "processed" ? now.toISOString() : null,
    refunded: false,
    simulated: true,
    instanceId,
    pld: buildPld({
      riskScore: status === "rejected" ? 80 : 20,
      riskLevel: status === "rejected" ? "high" : "low",
      screeningResult: status === "rejected" ? "blocked" : "clear",
      analystNotes: "Transação simulada (HML).",
    }),
    metadata: buildGenericMetadata({
      external_id: `EXT-SIM-${stamp}-${id}`,
      internal_id: `INT-${id}-${instanceId}`,
      correlation_id: `corr-sim-${id}`,
      provider_ref: `SIM-${type}-${Date.now()}`,
    }),
  };

  if (!MOCK_TRANSACTIONS[instanceId]) {
    MOCK_TRANSACTIONS[instanceId] = [];
  }
  MOCK_TRANSACTIONS[instanceId] = [txn, ...MOCK_TRANSACTIONS[instanceId]];
  return { ...txn };
};

export const buildBaasTransactionsCsv = (transactions = []) => {
  const header = [
    "id",
    "referenceId",
    "createdAt",
    "clientName",
    "type",
    "amount",
    "currency",
    "status",
    "direction",
  ];
  const escape = (value) => {
    const text = value == null ? "" : String(value);
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };
  const rows = transactions.map((txn) =>
    [
      txn.id,
      txn.referenceId,
      txn.createdAt,
      txn.clientName,
      txn.type,
      (Number(txn.amount) / 100).toFixed(2),
      txn.currency,
      txn.status,
      txn.direction,
    ]
      .map(escape)
      .join(","),
  );
  return [header.join(","), ...rows].join("\n");
};

export { MOCK_CLIENTS };
